import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {fromEvent, Observable, throwError} from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ToastrWrapperService } from '../../shared/services/toastr-wrapper.service';
import { AuthApi } from '../api/auth.api';
import { Authentication } from '../models/authentication.model';
import { AuthState } from '../state/auth.state';
import {UserService} from '../../shared/services/user.service';
import { LoginIds, RegisterRequirements, ResetPasswordMethod }
  from '../../main/modules/management/models/users-setting.model';
import {Captcha} from '../models/captcha.model';
import { UserRegister } from '../models/userRegister-model';
import { VersionInfo } from '../models/version.model';
import {NbDialogService} from '@nebular/theme';
import {
  UserDeactivatedErrorDialogComponent,
} from '../components/user-deactivated-error-dialog/user-deactivated-error-dialog.component';
import {MessageDialogComponent} from '../../shared/components/message-dialog/message-dialog.component';
import {SupporterContactInfoModel} from '../models/supporter-contact-info.model';
import * as CryptoJS from 'crypto-js';
import { DataSourceUploadProgressService } from '../../data-source/services/data-source-upload-progress.service';
import { ActiveSessionModalComponent } from '../../dashboard/components/dialogs/active-session-modal/active-session-modal.component';

@Injectable({
  providedIn: 'root',
})
export class AuthFacade {
  expirationTime = 172908000;

  private key = 'tart@!@#1234';
  private supporterContactInfo: SupporterContactInfoModel

  constructor(
    private authApi: AuthApi,
    private authState: AuthState,
    private router: Router,
    private toastr: ToastrWrapperService,
    private userService: UserService,
    private dialogService: NbDialogService,
    private progressService: DataSourceUploadProgressService
  ) {}

  login(
    email: string,
    password: string,
    userCaptchaKey: string,
    shareKey: string,
    isSuperAdmin: boolean = false,
    loginByTokenActive: boolean = false
  ) {
    this.editLoading(true);
    const loginRequest = isSuperAdmin
    ? this.authApi.superAdminLogin(email, password, userCaptchaKey, shareKey, loginByTokenActive)
    : this.authApi.login(
        email,
        password,
        userCaptchaKey,
        shareKey,
        loginByTokenActive
      );

    loginRequest
      .pipe(
        tap((resData) => {
          if (!resData.twoFactorEnabled) {
            this.handleAuthentication(resData);
          } else {
            this.handleTwoFactorAuthentication(resData.referenceId, resData.phoneNumber);
          }
        }),
      )
      loginRequest.subscribe(
        (res) => {
          if (res.hasUserActiveToken && !loginByTokenActive) {
            this.editLoading(false);
            this.openActiveSessionModal(
              email,
              password,
              userCaptchaKey,
              shareKey,
              isSuperAdmin
            );
            return;
          }

          if (!res.twoFactorEnabled) {
            this.handleAuthentication(res);
            this.userService.loadUserMinInfo();
          } else {
            this.handleTwoFactorAuthentication(
              res.referenceId,
              res.phoneNumber
            );
          }

          this.editLoading(false);
        },
          (errorMessage) => {
          this.editLoading(false);
          if(errorMessage.error.statusCode === 8){
            this.dialogService.open(UserDeactivatedErrorDialogComponent,
              {closeOnBackdropClick: false, context:{supporterContactInfo: this.supporterContactInfo}});
              this.checkCaptchaRequired()
          }else if (errorMessage.error.statusCode === 10){
            // this.loadCaptcha();
            this.checkCaptchaRequired()
            this.toastr.showError(errorMessage.error.message);
          }else if (errorMessage.error.showPopUp) {
            this.dialogService.open(MessageDialogComponent,{
              closeOnBackdropClick: false,
              context: {message: errorMessage.error.message, icon: 'error', supporterContactInfo: this.supporterContactInfo},
            });
          } else{
            this.toastr.showError(errorMessage.error.message);
            this.checkCaptchaRequired()
          }
        },
      );
  }
  private openActiveSessionModal(
    email: string,
    password: string,
    userCaptchaKey: string,
    shareKey: string,
    isSuperAdmin: boolean
  ) {
    const dialogRef = this.dialogService.open(
      ActiveSessionModalComponent,
      {
        closeOnBackdropClick: false,
      }
    );

    dialogRef.onClose.subscribe((result) => {
      if (result === 'continue') {
        this.login(
          email,
          password,
          userCaptchaKey,
          shareKey,
          isSuperAdmin,
          true
        );
      }
    });
  }

  verifyTwoFactorOTP(otp: string, referenceId: string): void {
    this.authApi.verifyTwoFactorOTP(otp, referenceId).pipe(tap((resData) => {
      this.handleAuthentication(resData);
    })).subscribe(
      (res) => {
        this.userService.loadUserMinInfo();
        this.editLoading(false);
        this.authState.updateConfirmationOTP(true);
      },
      () => {
        this.editLoading(false);
        this.authState.updateConfirmationOTP(false);
      },
    );
  }

  openForgetPasswordDialog(): void{
    this.dialogService.open(MessageDialogComponent,
      {closeOnBackdropClick: false, context:{icon: 'info', title: 'اطلاع‌رسانی',
          message: 'برای دریافت اطلاعات ورود با پشتیبانی ارتباط بگیرید.',supporterContactInfo: this.supporterContactInfo}});
  }

  register(user: UserRegister, shareKey: string): void {
    this.authApi.register(user, shareKey).subscribe(
      (response) => {
        if(response.data.shouldVerifyPhoneNumber) {
          this.authState.setOriginalPhoneNumber(user.cellPhoneNumber);
          this.authState.setPhoneNumber(response.data.phoneNumber);
          this.authState.setReferenceId(response.data.referenceId);
          this.router.navigate(['/auth/register/otp']);
        } else {
          this.toastr.showSuccess(
              'ثبت‌نام شما با موفقیت انجام شد. شما می‌توانید پس از تایید ثبت‌نام توسط راهبر سیستم از سامانه استفاده کنید.',
          );
          this.router.navigate(['/auth/login']);
        }
      },
      (errorRes) => {
        if (errorRes.error?.showPopUp) {
          this.dialogService.open(MessageDialogComponent,
            {
              closeOnBackdropClick: false,
              context: { message: errorRes.error.message, title: 'خطا', icon: 'error', supporterContactInfo: this.supporterContactInfo },
            });
        } else {
          this.toastr.showError(errorRes.error.message);
        }
        this.editLoading(false);
        this.loadCaptcha();
      },
    );
  }

  verifyPhoneNumberRegister(otp: string, phoneNumber: string, referenceId: string): void {
    this.authApi.verifyPhoneNumberRegistration(otp, phoneNumber, referenceId).subscribe(
        () => {
          this.authState.updateConfirmationOTP(true);
          this.resetConfirmationOTP();
        },
        () => {
          this.authState.updateConfirmationOTP(false);
        },
    );
  }

  loadCaptcha(): void {
    this.authApi.getCaptchaUniqueGuid().subscribe(
        (response) => {
          this.authState.updateCaptcha(response.data.key);
        },
    );
  }

  getCaptcha$(): Observable<Captcha> {
    return this.authState.getCaptcha$();
  }

  autoLogin(): boolean {
    const user = JSON.parse(this.getLocalStorageData('userData'));
    if (user) {
      this.updateAuthState(user);
      this.checkToken();
      this.userService.loadUserMinInfo();
      return true;
    }
    this.authState.isAuthenticated(false);
    return false;
  }

  logout() {
    if (!this.authState.expireIn) {
      clearTimeout(this.authState.expireIn);
    }
    this.authApi.logout().subscribe(() => {
        this.resetStates();
        this.progressService.closeModal();
        this.router.navigate(['/auth/login']);
    });
  }

  autoLogout() {
    this.authState.updateExpireIn(
      setTimeout(() => {
        this.logout();
      }, this.expirationTime),
    );
  }

  autoLogoutAlltabs() {
    window.addEventListener('storage', (event) => {
      if (event.storageArea === localStorage) {
        const user = this.getLocalStorageData('userData');
        const shareDashboard = this.router.url.split('/')[1] === 'share-dashboard';
        if (!user && !shareDashboard) {
          this.router.navigate(['/auth/login']);
        }
      }
    });
  }

  refreshToken(data: Partial<Authentication>) {
    data.expirationTime = this.expirationTime;
    this.updateAuthState(data);
  }

  resetStates() {
    localStorage.removeItem('userData');
    localStorage.removeItem('lastOnlineUrl');
    this.authState.updateExpireIn(null);
    this.authState.isAuthenticated(false);
    this.userService.updateUserMinState(null);
  }

  loadUserAuthentication$(): Observable<Partial<Authentication>> {
    return fromEvent<StorageEvent>(window, 'storage').pipe(map(event => {
      if(event.key === 'userData') {
        return JSON.parse(event.newValue);
      }
    }));
  }

  updateUserAuthentication(userAuthentication: Partial<Authentication>): void {
    this.authState.updateUserAuthentication(userAuthentication);
  }

  getUserAuthentication$(): Observable<Partial<Authentication>> {
    return this.authState.getUserAuthentication$();
  }

  editLoading(loading: boolean): void {
    this.authState.editLoading(loading);
  }

  checkToken(): void {
    this.authApi.checkToken().subscribe(
      (res) => {
        this.authState.isAuthenticated(true);
      },
      (err) => {
        const shareDashboard = this.router.url.split('/')[1] === 'share-dashboard';
        if(err.status === 401 && !shareDashboard) {
          this.resetStates();
          this.router.navigate(['auth/login']);
        }
      },
      () => {},
    );
  }

  getAuthentication$(): Observable<any> {
    return this.authState.getAuthentication$();
  }

  getLoading$(): Observable<any> {
    return this.authState.getLoading$();
  }

  loadActiveIdentitySettings() {
    this.authApi.getActiveIdentitySettings().pipe(map(res => res.data)).subscribe(
      (resData: any) => {
        this.authState.setLoginIdsSettings(resData.loginIds);
        this.authState.setRegisterRequirements(resData.requirements);
        this.authState.setResetPasswordMethod(resData.resetPasswordMethod);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getLoginIdsSettings$(): Observable<LoginIds[]> {
    return this.authState.getLoginIdsSettings$();
  }

  getRegisterRequirements$(): Observable<RegisterRequirements[]> {
    return this.authState.getRegisterRequirements$();
  }

  getResetPasswordMethod$(): Observable<ResetPasswordMethod> {
    return this.authState.getResetPasswordMethod$();
  }

  loadRegisterByUser() {
    this.authApi.getRegisterByUser().pipe(map(res => res.data)).subscribe(
      (res: any) => {
        const {registerByUser, supporterContactInfo, licenseSettings} = res;
        this.authState.setGeneralSettings(res);
        this.authState.setRegisterByUser(registerByUser);
        this.authState.setSupporterContactInfo(supporterContactInfo);
        this.supporterContactInfo = supporterContactInfo
        if(!licenseSettings?.isValid){
          this.router.navigate(['/licence-error']);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getSupporterContactInfo(): Observable<SupporterContactInfoModel> {
    return this.authState.getSupporterContactInfo();
  }

  getRegisterByUser$(): Observable<boolean> {
    return this.authState.getRegisterByUser$();
  }

  loadVersionInfo(): void {
    this.authApi.getVersion().pipe(map(res => res.data)).subscribe(
      (version: VersionInfo) => {
        this.authState.setVersionInfo(version);
      },
    );
  }

  getVersionInfo$(): Observable<VersionInfo> {
    return this.authState.getVersionInfo$();
  }

  getGeneralSettings$(): Observable<any> {
    return this.authState.getGeneralSettings$();
  }

  getReferenceId$(): Observable<string> {
    return this.authState.getReferenceId$();
  }

  getPhoneNumber$(): Observable<string> {
    return this.authState.getPhoneNumber$();
  }

  getOriginalPhoneNumber$(): Observable<string> {
    return this.authState.getOriginalPhoneNumber$();
  }

  getConfirmationOTP$(): Observable<boolean> {
    return this.authState.getConfirmationOTP$();
  }

  forgetPassword(loginId: string): void {
    this.authState.editLoading(true);
    this.authApi.forgetPassword(loginId).pipe(map(res => res.data)).subscribe(
        (data) => {
          this.authState.setPhoneNumber(data.phoneNumber);
          this.authState.setReferenceId(data.referenceId);
          this.authState.editLoading(false);
          this.authState.setErrorMessage(null);
        },
        (errorRes) => {
          if (errorRes.error.showPopUp) {
            this.authState.setErrorMessage(null);
            this.dialogService.open(MessageDialogComponent, {
              closeOnBackdropClick: false,
              context: {
                message: errorRes.error.message,
                icon: 'error',
                title: 'خطا',
                supporterContactInfo: this.supporterContactInfo,
              },
            });
          }
          if (errorRes.error.statusCode === 3) {
            this.authState.setErrorMessage(errorRes.error.message);
          }
          this.authState.editLoading(false);
        },
    );
  }

  verifyForgetPasswordOTP(otp: string, referenceId: string): void {
    this.authApi.verifyForgetPasswordOTP(otp, referenceId).pipe(map(res => res.data)).subscribe(
        (data) => {
          this.authState.updateConfirmationOTP(true);
          this.resetConfirmationOTP();
          this.authState.setReferenceId(data.referenceId);
        },
        () => {
          this.authState.updateConfirmationOTP(false);
        },
    );
  }

  setNewPassword(password: string, referenceId: string): void {
    this.authState.editLoading(true);
    this.authApi.setNewPassword(password, referenceId).subscribe(
        () => {
          this.toastr.showSuccess('گذرواژه شما با موفقیت تغییر یافت.');
          this.resetOTPStates();
          this.authState.editLoading(false);
          this.router.navigate(['auth/login']);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
          this.authState.editLoading(false);
        },
    );
  }

  resetConfirmationOTP(): void {
    this.authState.updateConfirmationOTP(null);
  }

  resendOTP(referenceId: string): void {
    this.authApi.resendOTP(referenceId).subscribe(
        (response) => {
          this.authState.setReferenceId(response.data.referenceId);
          this.authState.updateConfirmationOTP(null);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
    );
  }
  checkCaptchaRequired(): void {
    this.loadCaptcha();
    this.authApi.checkCaptchaRequired().pipe(
      tap((res) => {
        if (res.data.isCaptchaRequired) {
          this.authState.updateCaptchaRequired(true);
        } else {
          this.authState.updateCaptchaRequired(false);
        }
      })
    ).subscribe(
      () => {},
      (error) => {
      }
    );
  }

  setTimerInSecond(timerInSecond: number): void {
    this.authState.setTimerInSecond(timerInSecond);
  }

  getTimerInSecond$(): Observable<number> {
    return this.authState.getTimerInSecond$();
  }

  getErrorMessage$(): Observable<string> {
    return this.authState.getErrorMessage$();
  }

  resetOTPStates(): void {
    this.resetConfirmationOTP();
    this.authState.setReferenceId(null);
    this.authState.setPhoneNumber(null);
    this.authState.setOriginalPhoneNumber(null);
  }

  updateAuthState(user: Partial<Authentication>) {
    this.saveLocalStorageData('userData', JSON.stringify(user));
    this.autoLogout();
  }

  getLocalStorageData(key: string): string {
    const data = localStorage.getItem(key) || '';
    return this.decrypt(data) || null;
  }


  setSurveyStatus(payload:any): void {
    this.authApi.setSurveyStatus(payload).subscribe(
        (response) => {
            if (response.isSuccess) {
                // this.authState.setSurveyStatus(isSurveyDone);
                this.toastr.showSuccess();
            } else {
                this.toastr.showError();
            }
        },
        (errorRes) => {
            this.toastr.showError();
        }
    );
   }

   getSurveyStatus() {
      this.authApi.getSurveyStatus().pipe(
          map(res => res.data)
      ).subscribe(res=>{
        this.authState.setSurveyDetails(res);
      })
   }

   getSsoToken(code: string, state: string, instanceName: string): any {
    // just for PDB and INSO
    if (instanceName?.toLowerCase() === 'pdn' || instanceName?.toLowerCase() === 'inso') {
      const storedStateEnc = sessionStorage.getItem('sso_state');
      const storedVerifierEnc = sessionStorage.getItem('sso_verifier');

      let storedState: string | null = null;
      let verifier: string | null = null;

      try {
        if (storedStateEnc && storedVerifierEnc) {
          storedState = this.decrypt(storedStateEnc);
          verifier = this.decrypt(storedVerifierEnc);
        }
      } catch (e) {
        sessionStorage.removeItem('sso_state');
        sessionStorage.removeItem('sso_verifier');
      }

      // security: check state
      if (!storedState || state !== storedState) {
        sessionStorage.removeItem('sso_state');
        sessionStorage.removeItem('sso_verifier');
        this.toastr.showError('ورود ناموفق بود. دوباره تلاش کنید.');
        this.router.navigate(['/auth/login']);
        return;
      }

      return this.authApi.getSsoToken(code, state, verifier).pipe(
        tap({
          next: () => {
            sessionStorage.removeItem('sso_state');
            sessionStorage.removeItem('sso_verifier');
          },
          error: () => {
            sessionStorage.removeItem('sso_state');
            sessionStorage.removeItem('sso_verifier');
          }
        })
      );
    }

    // for others like FIU and SGBI
    return this.authApi.getSsoToken(code, state);
  }


  getSsoAuthorizeLink(ssoSetting: any, prevState:string): Promise<string> {
    return new Promise(async (resolve) => {
      if (!ssoSetting) return resolve('');

      const { clientId, url, instanceName } = ssoSetting;
      const appOrigin = window.location.origin;
      const redirectUrl = `${appOrigin}/auth/login`;

      // SSO type 1: Simple (SGBI) & (FIU)
      if (instanceName?.toLowerCase() === 'sgbi') {
        const authorizeLink =
          `${url}?response_type=code` +
          `&scope=openid%20profile` +
          `&client_id=${clientId}` +
          `&state=state1` +
          `&redirect_uri=${redirectUrl}`;
        return resolve(authorizeLink);
      }

      if (instanceName?.toLowerCase() === 'fiu') {
        const state = prevState || this.randomUrlSafe(16);  
        const authorizeLink =
          `${url}?response_type=code` +
          `&scope=openid%20profile` +
          `&client_id=${clientId}` +
          `&state=${state}` +
          `&redirect_uri=${redirectUrl}`;
        return resolve(authorizeLink);
      }

      // SSO type 2: PKCE-based (Inso)
      if (instanceName?.toLowerCase() === 'inso') {
        // If prevState exists, it means we’ve returned from the callback and must NOT generate a new verifier
        const existingVerifierEnc = prevState ? sessionStorage.getItem('sso_verifier') : null;
        let codeVerifier: string;
        let codeChallenge: string;
        const state = prevState || this.randomUrlSafe(16);

        if (existingVerifierEnc) {
          try {
            codeVerifier = this.decrypt(existingVerifierEnc);
            codeChallenge = await this.sha256Of(codeVerifier);
          } catch (e) {
            console.warn('Failed to reuse existing verifier, regenerating new one', e);
            codeVerifier = this.randomUrlSafe(32);
            codeChallenge = await this.sha256Of(codeVerifier);
          }
        } else {
          codeVerifier = this.randomUrlSafe(32);
          codeChallenge = await this.sha256Of(codeVerifier);
        }

         // Only store new values in session when prevState does not exist
        if (!prevState) {
          try {
            sessionStorage.setItem('sso_state', this.encrypt(state));
            sessionStorage.setItem('sso_verifier', this.encrypt(codeVerifier));
          } catch (e) {
            console.error('Failed to store SSO data in sessionStorage', e);
          }
        }
        const authorizeLink =
          `${url}/connect/authorize?client_id=${clientId}` +
          `&response_type=code` +
          `&redirect_uri=${redirectUrl}` +
          `&scope=openid profile tart.fullaccess estsecurity.read offline_access` +
          `&code_challenge=${codeChallenge}` +
          `&code_challenge_method=S256` +
          `&state=${state}`;
        return resolve(authorizeLink);
      }
      // SSO type 3: PKCE-based (S)
      if (instanceName?.toLowerCase() === 'pdn') {
        const codeVerifier = this.randomUrlSafe(32);
        const codeChallenge = await this.sha256Of(codeVerifier);
        const state = prevState || this.randomUrlSafe(16);
        try {
          sessionStorage.setItem('sso_state', this.encrypt(state));
          sessionStorage.setItem('sso_verifier', this.encrypt(codeVerifier));
        } catch (e) {
          console.error('Failed to store SSO PKCE data in sessionStorage', e);
        }
        const authorizeLink =
          `${url}/connect/authorize?client_id=${clientId}` +
          `&response_type=code` +
          `&redirect_uri=${redirectUrl}` +
          `&scope=openid profile tart.fullaccess estsecurity.read` +
          `&code_challenge=${codeChallenge}` +
          `&code_challenge_method=S256` +
          `&state=${state}`;
        return resolve(authorizeLink);
      }

      resolve('');
    });
    
  }

private base64UrlEncodeBytes(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  let base64 = window.btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

private randomUrlSafe(length: number): string {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return this.base64UrlEncodeBytes(bytes);
}

private async sha256Of(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashBytes = new Uint8Array(hashBuffer);
  return this.base64UrlEncodeBytes(hashBytes);
}


  getSurveyStatus$(): Observable<boolean> {
    return this.authState.getSurveyStatus$();
  }

  getSurveyDetails$(): Observable<any> {
    return this.authState.getSurveyDetails$();
  }


  private saveLocalStorageData(key: string ,value: string): void {
    localStorage.setItem(key, this.encrypt(value));
  }

  private encrypt(txt: string): string {
    return CryptoJS.AES.encrypt(txt, this.key).toString();
  }

  private decrypt(txtToDecrypt: string): string {
    return CryptoJS.AES.decrypt(txtToDecrypt, this.key).toString(CryptoJS.enc.Utf8);
  }

  handleAuthentication(resData: any) {
    const user: Partial<Authentication> = {
      accessToken: resData.access_token,
      refreshToken: resData.refresh_token,
      expiresIn: resData.expires_in,
      tokenType: resData.token_type,
      expirationTime: this.expirationTime,
      tokenIsRefreshing: false,
    };
    this.updateAuthState(user);
    if (resData.customLoginPage?.dashboardId && resData.customLoginPage?.workspaceId) {
      this.authState.isAuthenticated(true);
      this.router.navigate(['dashboard/view',resData.customLoginPage.workspaceId,resData.customLoginPage?.dashboardId]);
    } else {
      this.authState.isAuthenticated(true);
    }
  }

  private handleTwoFactorAuthentication(referenceId: string, phoneNumber: string): void {
    this.authState.setReferenceId(referenceId);
    this.authState.setPhoneNumber(phoneNumber);
    this.editLoading(false);
    this.router.navigate(['auth/login/otp']);
  }
}
